import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Wallet } from '@phosphor-icons/react';
import { toast } from 'sonner';

import { ArchivedCollapsibleSection } from '../../components/common/ArchivedCollapsibleSection';
import { EmptyState } from '../../components/common/EmptyState';
import { PageContentShell } from '../../components/common/PageContentShell';
import { SectionSkeleton } from '../../components/common/SectionSkeleton';
import { IncomeSourceBucketGroup } from '../../components/income/IncomeSourceBucketGroup';
import { IncomeSourceRow } from '../../components/income/IncomeSourceRow';
import { IncomeSourcesSummaryCard } from '../../components/income/IncomeSourcesSummaryCard';
import { IncomeSourceWizardSheet } from '../../components/income/IncomeSourceWizardSheet';
import { MOBILE_PAGE_PADDING_WITH_STICKY_CLASS } from '../../components/layout/mobileLayoutConstants';
import { Button } from '../../components/ui/Button';
import { useIncomeSourcesQuery, useUpdateIncomeSourceMutation } from '../../features/income/queries';
import { useQueryShellState } from '../../hooks/useQueryShellState';
import { buildIncomeSourcesPageModel } from '../../lib/incomeSourcesDisplay';
import { cn } from '../../utils/cn';
import pt from '../../locales/pt';

function archivedCountText(count) {
  return count === 1
    ? pt.income.sources.summaryCard.countOne
    : pt.income.sources.summaryCard.countMany.replace('{count}', String(count));
}

export function IncomeSources() {
  const { data: sources, isPending, isFetching } = useIncomeSourcesQuery();
  const sourcesList = useMemo(() => sources ?? [], [sources]);
  const sourcesShell = useQueryShellState({ data: sources, isPending, isFetching });
  const updateMutation = useUpdateIncomeSourceMutation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [createBucketId, setCreateBucketId] = useState(null);

  const activeSources = useMemo(() => sourcesList.filter((source) => source.is_active !== false), [sourcesList]);
  const archivedSources = useMemo(
    () => sourcesList.filter((source) => source.is_active === false).sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0) || (a.name || '').localeCompare(b.name || '', 'pt')),
    [sourcesList],
  );
  const pageModel = useMemo(() => buildIncomeSourcesPageModel({ sources: sourcesList }), [sourcesList]);

  useEffect(() => {
    if (searchParams.get('create') !== '1') return;
    setEditing(null);
    setCreateBucketId(searchParams.get('bucket') || null);
    setSheetOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('create');
    next.delete('bucket');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const startCreate = (bucketId = null) => {
    setEditing(null);
    setCreateBucketId(bucketId || null);
    setSheetOpen(true);
  };

  const startEdit = (source) => {
    setCreateBucketId(null);
    setEditing(source);
    setSheetOpen(true);
  };

  const closeSheet = () => {
    setSheetOpen(false);
    setEditing(null);
    setCreateBucketId(null);
  };

  const updateActiveState = (source, is_active, message) => {
    updateMutation.mutateAsync({ id: source.id, is_active })
      .then(() => toast.success(message.replace('{name}', source.name)))
      .catch(() => toast.error(is_active ? 'Não foi possível reactivar.' : 'Não foi possível arquivar.'));
  };

  const isFullyEmpty = activeSources.length === 0 && archivedSources.length === 0;
  const [firstGroup, ...restGroups] = pageModel.groups;

  return (
    <div className={cn('relative flex w-full min-w-0 flex-col gap-space-5 overflow-x-hidden', MOBILE_PAGE_PADDING_WITH_STICKY_CLASS)}>
      <header className="hidden items-center justify-between gap-space-4 md:flex">
        <div className="flex flex-col gap-0.5">
          <h1 className="type-title-page text-foreground">{pt.income.sources.pageTitle}</h1>
          <p className="type-body-sm text-muted-foreground">{pt.income.sources.subtitle}</p>
        </div>
        <Button type="button" size="sm" onClick={() => startCreate()} className="shrink-0">
          <Plus className="mr-1 h-4 w-4" aria-hidden />
          {pt.income.sources.addNew}
        </Button>
      </header>

      <PageContentShell shells={[sourcesShell]} fallback={<SectionSkeleton variant="list" />}>
        {isFullyEmpty ? (
          <EmptyState
            icon={Wallet}
            title={pt.income.sources.empty.title}
            description={pt.income.sources.empty.subtitle}
            action={<Button onClick={() => startCreate()}><Plus className="mr-1 h-4 w-4" aria-hidden />{pt.income.sources.empty.cta}</Button>}
          />
        ) : (
          <div className="flex flex-col gap-space-5">
            <IncomeSourcesSummaryCard totalEur={pageModel.totalEur} groups={pageModel.groups} />

            {activeSources.length > 0 ? (
              <div className="flex flex-col gap-space-3">
                <p className="px-1 type-section text-muted-foreground">{pt.income.sources.listSection}</p>
                <div className="flex flex-col gap-space-5 lg:flex-row lg:items-start">
                  {firstGroup ? (
                    <div className="flex flex-1 flex-col gap-space-3">
                      <IncomeSourceBucketGroup
                        bucket={firstGroup.bucket}
                        sources={firstGroup.sources}
                        onEdit={startEdit}
                        onArchive={(source) => updateActiveState(source, false, pt.income.sources.archivedToast)}
                      />
                    </div>
                  ) : null}
                  {restGroups.length > 0 ? (
                    <div className="flex flex-1 flex-col gap-space-3">
                      {restGroups.map((group) => (
                        <IncomeSourceBucketGroup
                          key={group.bucket.id}
                          bucket={group.bucket}
                          sources={group.sources}
                          onEdit={startEdit}
                          onArchive={(source) => updateActiveState(source, false, pt.income.sources.archivedToast)}
                        />
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            ) : (
              <p className="rounded-ds-lg border border-border bg-card p-space-5 text-center type-body-sm text-muted-foreground">
                {pt.income.sources.allArchivedHint}
              </p>
            )}

            <ArchivedCollapsibleSection
              title={pt.common.archivedSection.title}
              countText={archivedCountText(archivedSources.length)}
              count={archivedSources.length}
            >
              <p className="type-label text-muted-foreground">{pt.income.sources.archivedHint}</p>
              <ul className="flex flex-col">
                {archivedSources.map((source, index) => (
                  <li key={source.id} className="list-none">
                    <IncomeSourceRow
                      source={source}
                      isFirst={index === 0}
                      onEdit={startEdit}
                      onRestore={(item) => updateActiveState(item, true, pt.income.sources.restoredToast)}
                    />
                  </li>
                ))}
              </ul>
            </ArchivedCollapsibleSection>
          </div>
        )}
      </PageContentShell>

      <IncomeSourceWizardSheet isOpen={sheetOpen} onClose={closeSheet} initialValues={editing} initialBucketId={createBucketId} />
    </div>
  );
}

export default IncomeSources;
